// Part 1 (lines 1-200)
/*
  ----------------------------------------------------------------------------
  GenerativeSearch.js
  ----------------------------------------------------------------------------
  Below is the updated GenerativeSearch.js, broken into numbered chunks.
  This is Part 1 (lines 1-200).
  The Dashboard has been updated to match the provided screenshot exactly.
*/

import React, { useState } from 'react';
import './GenerativeSearch.css'; // Ensure this path matches your file structure

// ——— Main Component ———
const NAV = [
  'Dashboard',
  'Prompt Lab',
  'Optimizer',
  'Index Tracker',
  'A/B Tests',
  'Settings',
];

export default function GenerativeSearch() {
  const [section, setSection] = useState('Dashboard');
  const [openAIKey, setOpenAIKey] = useState('');

  return (
    <div className="app">
      {/* ───── Sidebar ───── */}
      <aside className="sidebar">
        {/* ADDED: renamed class */}
        <div className="gseo-logo">GSEO</div>
        <nav className="nav">
          <ul>
            {NAV.map(item => (
              <li key={item} className={section === item ? 'active' : ''}>
                <a onClick={() => setSection(item)}>{item}</a>
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      {/* ───── Main Content ───── */}
      <main className="content">
        {section === 'Dashboard' && <Dashboard />}
        {section === 'Prompt Lab' && <PromptLab />}
        {section === 'Optimizer' && <Optimizer />}
        {section === 'Index Tracker' && <IndexTracker />}
        {section === 'A/B Tests' && <ABTests />}
        {section === 'Settings' && (
          <Settings
            openAIKey={openAIKey}
            setOpenAIKey={setOpenAIKey}
          />
        )}
      </main>
    </div>
  );
}

// --------------------------------------------------------------------------
// Dashboard Page (updated to match screenshot design)
// --------------------------------------------------------------------------
function Dashboard() {
  // Top metrics
  const metrics = [
    { title: 'AI Visibility Score', value: '82', sub: 'GPT' },
    { title: 'Indexed Content', value: '456' },
    { title: 'Prompts Generated', value: '1,204' },
    { title: 'Feedback Score', value: '4.5', sub: 'Claude' },
  ];

  // User Prompt Tracker data
  const promptRows = [
    { text: 'How can improve sleep quality?', intent: 'Informational' },
    { text: 'Explain blockchain technology:', intent: 'Informational' },
    { text: 'What are the benefits of meditation?', intent: 'Navigational' },
    { text: 'Suggest budget-friendly travel destinations.', intent: 'Transactional' },
  ];

  // LLM-driven suggestions
  const suggestions = [
    'Enhance the introduction with a concise subintro of Nirv birdit.',
    'Incorporate relevant internal links to related sections.',
    'Improve readability by using shorter paragraphs and bullet points.',
  ];

  // Content Grading data
  const gradingBars = [
    { label: 'AI Readability', pct: 80 },
    { label: 'Prompt-Trigger Likelihood', pct: 60 },
    { label: 'Semantic Coverage', pct: 75 },
  ];

  // AI Indexing Tracker data
  const bots = [
    { name: 'OpenAI GPTBot', ok: true },
    { name: 'Anthropic Claude', ok: false },
    { name: 'Perplexity Bot', ok: true },
  ];

  return (
    <div>
      {/* ===== Page Title ===== */}
      <h1 className="dashboard-title">Dashboard</h1>

      {/* ===== Top Metric Cards ===== */}
      <div className="stats-grid dashboard-metrics">
        {metrics.map((m) => (
          <div key={m.title} className="stat-card">
            <div className="label">{m.title}</div>
            <div className="value">{m.value}</div>
            {m.sub && <div className="badge">{m.sub}</div>}
          </div>
        ))}
      </div>

      {/* ===== Prompt & Intent Lab Row ===== */}
      <div className="two-column-row">
        {/* Prompt Tracker Card */}
        <div className="card prompt-tracker-card">
          <div className="card-header">Prompt &amp; Intent Lab</div>
          <div className="card-body">
            <div className="section">
              <div className="section-title">User Prompt Tracker</div>
              <ul className="prompt-list">
                {promptRows.map((r, i) => (
                  <li key={i}>
                    {r.text}
                    <span className="prompt-badge">{r.intent}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Prompt Template Builder Card */}
        <div className="card prompt-template-card">
          <div className="card-header">Prompt &amp; Intent Lab</div>
          <div className="card-body">
            <div className="section">
              <select className="template-select">
                <option>(If a user asks…)</option>
              </select>
              <p className="template-example">
                To improve sleep quality, you can try following strategies.
              </p>
              <div className="template-buttons">
                <button className="btn-secondary">At comprising learn pure nativ...</button>
                <button className="btn-secondary">Suggest coaching suggest...</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== LLM-Driven Suggestions Row ===== */}
      <div className="two-column-row mt-24">
        {/* LLM Suggestions Card */}
        <div className="card llm-suggestions-card">
          <div className="card-header">LLM-Driven Suggestions</div>
          <div className="card-body">
            <ul className="suggestion-list">
              {suggestions.map((s, idx) => (
                <li key={idx}>{s}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* Content Optimizer Chart Card */}
        <div className="card chart-card">
          <div className="card-header">Content Optimizer</div>
          <div className="card-body">
            <div className="chart-placeholder">
              {/* Placeholder SVG chart */}
              <svg viewBox="0 0 400 120" className="chart">
                <path
                  d="M0 80 C50 20, 150 20, 200 60 S350 110, 400 60"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  opacity="0.4"
                />
                <path
                  d="M0 90 C50 40, 150 40, 200 75 S350 125, 400 75"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* ===== Content & Grading Row ===== */}
      <div className="two-column-row mt-24">
        {/* Content Optimizer Text Card */}
        <div className="card content-text-card">
          <div className="card-header">Content Optimizer</div>
          <div className="card-body">
            <textarea
              className="content-textarea"
              placeholder="Paste or write your content here..."
            />
            <button className="btn-primary mt-12">Analyze Content</button>
          </div>
        </div>

        {/* Grading & Index Tracker Column */}
        <div className="grading-index-column">
          {/* Content Grading Card */}
          <div className="card grading-card">
            <div className="card-header">Content Grading</div>
            <div className="card-body">
              {gradingBars.map((b) => (
                <div key={b.label} className="grading-item">
                  <span>{b.label}</span>
                  <div className="grading-bar">
                    <span style={{ width: `${b.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* AI Indexing Tracker Card */}
          <div className="card indexing-card mt-24">
            <div className="card-header">AI Indexing Tracker</div>
            <div className="card-body">
              {bots.map((bot) => (
                <div key={bot.name} className="indexing-row">
                  <span>{bot.name}</span>
                  <span className="index-status">
                    {bot.ok ? '✅ Completed' : '⚪️ Pending'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Prompt Lab Page (unchanged from prior version)
// --------------------------------------------------------------------------
function PromptLab() {
  const [promptInput, setPromptInput] = useState('');
  const trending = [
    { prompt: 'how to integrate ai agents', intent: 'informational' },
    { prompt: 'lex agent pricing', intent: 'transactional' },
  ];

  return (
    <div>
      <div className="section">
        <div className="section-title">Trending Prompts</div>
        <ul className="prompt-list">
          {trending.map((t, idx) => (
            <li
              key={idx}
              className="stat-card"
              style={{ display: 'flex', justifyContent: 'space-between' }}
            >
              <span>{t.prompt}</span>
              <span className="prompt-badge">{t.intent}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="section">
        <div className="section-title">Prompt Template Builder</div>
        <textarea
          className="card"
          placeholder="If a user asks __, then the AI should __..."
          value={promptInput}
          onChange={(e) => setPromptInput(e.target.value)}
          style={{ width: '100%', height: '120px', resize: 'vertical' }}
        />
        <button
          className="stat-card"
          style={{
            marginTop: '12px',
            background: 'var(--accent)',
            color: '#fff',
            border: 'none',
            cursor: promptInput.trim() ? 'pointer' : 'not-allowed',
            opacity: promptInput.trim() ? 1 : 0.6,
          }}
          disabled={!promptInput.trim()}
        >
          Generate Variants
        </button>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Content Optimizer Page (unchanged)
// --------------------------------------------------------------------------
function Optimizer() {
  const metrics = [
    { label: 'AI Readability', value: '75%' },
    { label: 'Prompt Trigger Likelihood', value: '60%' },
    { label: 'Semantic Coverage', value: '80%' },
  ];

  return (
    <div>
      <div className="section">
        <div className="section-title">Upload or Write Content</div>
        <textarea
          className="card"
          placeholder="Paste your Markdown / HTML here…"
          style={{ width: '100%', height: '120px', resize: 'vertical' }}
        />
        <button
          className="stat-card"
          style={{
            marginTop: '12px',
            background: 'var(--accent)',
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          Analyze Content
        </button>
      </div>

      <div className="stats-grid">
        {metrics.map((m) => (
          <div key={m.label} className="stat-card">
            <div className="label">{m.label}</div>
            <div className="value">{m.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Index Tracker Page (unchanged)
// --------------------------------------------------------------------------
function IndexTracker() {
  const bots = [
    { name: 'OpenAI GPTBot', last: '3 min ago', ok: true },
    { name: 'Anthropic Claude', last: '—', ok: false },
    { name: 'Perplexity Bot', last: '2 days', ok: true },
  ];

  return (
    <div>
      <div className="section">
        <div className="section-title">Crawler Activity</div>
        <table className="card" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: '#f9fafb' }}>
            <tr>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#4b5563' }}>Bot</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#4b5563' }}>Status</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#4b5563' }}>Last Seen</th>
            </tr>
          </thead>
          <tbody>
            {bots.map((b, i) => (
              <tr key={i} style={{ borderTop: '1px solid #e5e7eb' }}>
                <td style={{ padding: '12px' }}>{b.name}</td>
                <td style={{ padding: '12px' }}>{b.ok ? '✅ Completed' : '⚪️ Pending'}</td>
                <td style={{ padding: '12px' }}>{b.last}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <button
          className="stat-card"
          style={{
            marginTop: '12px',
            background: 'var(--accent)',
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          Submit Sitemap
        </button>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// A/B Tests Page (unchanged)
// --------------------------------------------------------------------------
function ABTests() {
  const [winner, setWinner] = useState(null);
  const variants = [
    { id: 'A', text: 'Variant A – Lorem ipsum dolor sit amet.' },
    { id: 'B', text: 'Variant B – Consectetur adipiscing elit.' },
  ];

  return (
    <div>
      <div className="section" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        {variants.map((v) => (
          <div key={v.id} className="stat-card" style={{ flex: '1 1 calc(50% - 16px)' }}>
            <div className="label">Variant {v.id}</div>
            <div style={{ margin: '8px 0', color: '#374151' }}>{v.text}</div>
            <button
              className="stat-card"
              style={{
                marginTop: '8px',
                background: 'var(--muted)',
                color: '#fff',
                border: 'none',
                cursor: 'pointer',
              }}
              onClick={() => setWinner(v.id)}
            >
              Select Winner
            </button>
          </div>
        ))}
      </div>

      {winner && (
        <div style={{ marginTop: '16px', fontSize: '14px', color: '#6b7280' }}>
          Winner selected: <span style={{ fontWeight: '600' }}>{winner}</span>
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// Settings Page (unchanged)
// --------------------------------------------------------------------------
function Settings({ openAIKey, setOpenAIKey }) {
  return (
    <div style={{ maxWidth: '400px' }}>
      <div className="section">
        <div className="section-title">API Keys</div>
        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>
          <span style={{ color: '#374151' }}>OpenAI API Key</span>
          <input
            type="text"
            className="card"
            placeholder="sk-..."
            value={openAIKey}
            onChange={(e) => setOpenAIKey(e.target.value)}
            style={{ width: '100%', marginTop: '4px', padding: '8px', boxSizing: 'border-box' }}
          />
        </label>
        <button
          className="stat-card"
          style={{
            marginTop: '12px',
            background: 'var(--accent)',
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          Save
        </button>
      </div>
    </div>
  );
}

// END PART 1 (lines 1-200)
// Part 2 (lines 201-250)
/*
  ----------------------------------------------------------------------------
  GenerativeSearch.js (continued)
  ----------------------------------------------------------------------------
  This is Part 2, consisting of lines 201-250 of the file.
  All original lines remain; no deletions were made.
*/

  // (No additional code beyond Settings component)

// END OF FILE (lines 201-250)
